import { useState } from "react";
import { openExternal } from "../utils/openExternal";
import { auth } from "../services/firebase";
import { writeRegistrationAcks } from "../services/legalAcks";
import { localDateString } from "../utils/entryDate";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogIn, UserPlus, Eye, EyeOff, Activity, Mail, ArrowLeft } from "lucide-react";
import { ROUTE_TITLES } from "../services/analytics";
import { usePageTracking } from "../hooks/usePageTracking";

const firebaseErrors: Record<string, string> = {
  "auth/invalid-email": "Érvénytelen email cím.",
  "auth/user-not-found": "Nem található felhasználó ezzel az email címmel.",
  "auth/wrong-password": "Helytelen jelszó.",
  "auth/email-already-in-use": "Ez az email cím már foglalt.",
  "auth/weak-password": "A jelszó legalább 6 karakter legyen.",
  "auth/too-many-requests": "Túl sok próbálkozás. Próbáld később.",
  "auth/invalid-credential": "Helytelen email vagy jelszó.",
  "auth/popup-closed-by-user": "A bejelentkezési ablak bezárult.",
  "auth/cancelled-popup-request": "A bejelentkezés megszakadt.",
  "auth/no-credential": "A bejelentkezés sikertelen volt. Próbáld újra.",
};

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="16" height="18" viewBox="0 0 814 1000" fill="white">
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-155.5-127.4C46.7 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 70.1 0 128.4 46.4 172.5 46.4 42.8 0 109.6-49.1 189.2-49.1 30.8 0 130.4 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
    </svg>
  );
}

// ── Email megerősítés képernyő ────────────────────────────────────────
function VerifyEmailScreen() {
  const { user, signOut, resendVerification } = useAuth();
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleResend = async () => {
    setLoading(true);
    try {
      await resendVerification();
      setSent(true);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex flex-col items-center justify-center px-4">
      <div className="flex items-center gap-2 mb-8">
        <div className="w-10 h-10 bg-emerald-700 rounded-xl flex items-center justify-center">
          <Activity size={20} className="text-white" />
        </div>
        <span className="text-2xl font-bold text-gray-900">GLP Napló</span>
      </div>

      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5 text-center">
        <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto">
          <Mail size={28} className="text-emerald-700" />
        </div>

        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-1">Erősítsd meg az email címed</h2>
          <p className="text-sm text-gray-500">
            Küldtünk egy megerősítő emailt erre a címre:
          </p>
          <p className="text-sm font-semibold text-gray-800 mt-1">{user?.email}</p>
        </div>

        <div className="space-y-2">
          <button
            onClick={handleReload}
            className="w-full bg-emerald-700 text-white py-3 rounded-xl font-semibold text-sm hover:bg-emerald-800 transition-colors"
          >
            Már megerősítettem →
          </button>

          <button
            onClick={handleResend}
            disabled={loading || sent}
            className="w-full border border-gray-200 text-gray-600 py-3 rounded-xl font-medium text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {sent ? "✅ Email elküldve!" : loading ? "Küldés..." : "Email újraküldése"}
          </button>
        </div>

        <button
          onClick={signOut}
          className="text-xs text-gray-400 hover:text-gray-600 hover:underline"
        >
          Kijelentkezés
        </button>
      </div>
    </div>
  );
}

// ── Jelszó visszaállítás képernyő ─────────────────────────────────────
function ResetPasswordScreen({ onBack }: { onBack: () => void }) {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err: any) {
      setError(firebaseErrors[err?.code] || "Hiba történt, próbáld újra.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
      >
        <ArrowLeft size={14} /> Vissza
      </button>

      <div>
        <h2 className="text-lg font-bold text-gray-900">Jelszó visszaállítása</h2>
        <p className="text-xs text-gray-500 mt-1">
          Add meg az email címed és küldünk egy visszaállítási linket.
        </p>
      </div>

      {sent ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center space-y-2">
          <p className="text-2xl">📬</p>
          <p className="text-sm font-semibold text-emerald-800">Email elküldve!</p>
          <p className="text-xs text-emerald-700">
            Nézd meg a postaládádat és kövesd az utasításokat.
          </p>
          <button
            onClick={onBack}
            className="mt-2 text-xs text-emerald-700 font-medium hover:underline"
          >
            Vissza a bejelentkezéshez
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Email cím</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nev@email.com"
              required
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-700 text-white py-3 rounded-xl font-semibold text-sm hover:bg-emerald-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? (
              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <><Mail size={16} /> Link küldése</>
            )}
          </button>
        </form>
      )}
    </div>
  );
}

// ── Fő AuthPage ───────────────────────────────────────────────────────
export default function AuthPage({ defaultMode = "login" }: { defaultMode?: "login" | "register" }) {
  const { signIn, signUp, signInWithGoogle, signInWithApple, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"login" | "register" | "reset">(defaultMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  usePageTracking(
    mode === "register" ? ROUTE_TITLES["/signup"] : ROUTE_TITLES["/signin"],
    mode === "register" ? "/signup" : "/signin"
  );

  // Ha be van jelentkezve de nem verified → verify screen
  if (user && !user.emailVerified && user.providerData[0]?.providerId === "password") {
    return <VerifyEmailScreen />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === "login") {
        await signIn(email, password);
      } else {
        await signUp(email, password);
        await rogzitRegisztraciosNyugtat();
      }
    } catch (err: any) {
      setError(firebaseErrors[err?.code] || "Ismeretlen hiba történt.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * REGISZTRÁCIÓS NYUGTA (ClickUp 123qmzqzk1r §7.3/d).
   *
   * Aki ezen a képernyőn elfogadta a jogi dokumentumokat (a `gdprAccepted`
   * jelölő kötelező a regisztrációhoz), ne kapjon rá BLOKKOLÓ ABLAKOT az
   * onboarding után — ugyanarról, amit az előző képernyőn elfogadott.
   *
   * ⚠️ MIT ír meg: azt, amit a kapu ÉPPEN kérne — a `writeRegistrationAcks`
   * ugyanazt a `resolveLegalGate`-et futtatja üres nyugta-halmazzal. Nem
   * értelmezi újra a feltételt; enélkül a regisztráció és a kapu MÁST
   * gondolhatna, és egy bejelentési ív közepén regisztráló felhasználó a még
   * NEM HATÁLYOS verziót is lenyugtázná — a hatálybalépés napján pedig a kapu
   * csendben kimaradna nála.
   *
   * ⚠️ MIÉRT NEM BLOKKOL a hiba: a regisztráció nem hasalhat el azon, hogy a
   * nyugta nem íródott meg. Ha elhasal, a kapu később elkéri — késés, nem
   * adatvesztés. Ugyanaz a fail-open elv, mint a kapu többi pontján.
   *
   * ⚠️ CSAK register módban hívjuk: a Google/Apple gomb BEJELENTKEZÉSRE is
   * szolgál, és egy visszatérő felhasználó most nem fogadott el semmit.
   */
  const rogzitRegisztraciosNyugtat = async () => {
    if (mode !== "register") return;
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    await writeRegistrationAcks(uid, localDateString(new Date()));
  };

  const goToFooldal = () => {
    navigate("/fooldal");
  };

  const handleGoogle = async () => {
    if (mode === "register" && !gdprAccepted) return;
    setError(null);
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
      await rogzitRegisztraciosNyugtat();
      goToFooldal();
    } catch (err: any) {
      setError(firebaseErrors[err?.code] || "Google bejelentkezés sikertelen.");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleApple = async () => {
    if (mode === "register" && !gdprAccepted) return;
    setError(null);
    setAppleLoading(true);
    try {
      await signInWithApple();
      await rogzitRegisztraciosNyugtat();
      goToFooldal();
    } catch (err: any) {
      setError(firebaseErrors[err?.code] || "Apple bejelentkezés sikertelen.");
    } finally {
      setAppleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex flex-col items-center justify-center px-4">
      {/* Logo */}
      <div className="flex items-center gap-2 mb-8">
        <div className="w-10 h-10 bg-emerald-700 rounded-xl flex items-center justify-center">
          <Activity size={20} className="text-white" />
        </div>
        <span className="text-2xl font-bold text-gray-900">GLP Napló</span>
      </div>

      {mode === "reset" ? (
        <ResetPasswordScreen onBack={() => setMode("login")} />
      ) : (
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
          {/* Tab */}
          <div className="flex bg-gray-100 rounded-xl p-1">
            {(["login", "register"] as const).map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(null); }}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                  mode === m
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {m === "login" ? "Bejelentkezés" : "Regisztráció"}
              </button>
            ))}
          </div>

          {/* Google gomb */}
          <button
            onClick={handleGoogle}
            disabled={googleLoading || appleLoading || (mode === "register" && !gdprAccepted)}
            className="w-full flex items-center justify-center gap-3 border border-gray-200 rounded-xl py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60"
          >
            {googleLoading ? (
              <span className="animate-spin w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full" />
            ) : (
              <GoogleIcon />
            )}
            Folytatás Google-lel
          </button>

          {/* Apple gomb — fekete, Apple brand guideline */}
          <button
            onClick={handleApple}
            disabled={appleLoading || googleLoading || (mode === "register" && !gdprAccepted)}
            className="w-full flex items-center justify-center gap-3 bg-black rounded-xl py-2.5 text-sm font-medium text-white hover:bg-gray-900 transition-colors disabled:opacity-60"
          >
            {appleLoading ? (
              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <AppleIcon />
            )}
            Folytatás Apple-lel
          </button>

          {/* Elválasztó */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-100" />
            <span className="text-xs text-gray-400">vagy email-lel</span>
            <div className="flex-1 h-px bg-gray-100" />
          </div>

          {/* Email / jelszó form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Email cím</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nev@email.com"
                required
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition"
              />
            </div>

            <div>
              <label className="text-xs text-gray-500 block mb-1">Jelszó</label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "register" ? "min. 6 karakter" : "••••••••"}
                  required
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {mode === "login" && (
                <button
                  type="button"
                  onClick={() => { setMode("reset"); setError(null); }}
                  className="text-xs text-emerald-700 hover:underline mt-1.5 block"
                >
                  Elfelejtett jelszó?
                </button>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                <p className="text-xs text-red-700">{error}</p>
              </div>
            )}

            {/* Adatvédelmi checkbox — csak regisztrációnál.

                ⚠️ A két jogi hivatkozás SZÁNDÉKOSAN a <label>-en KÍVÜL van.
                A <button> labelable elem, tehát a <label> belsejében
                érvénytelen markupot adna (egy label nem tartalmazhat a
                címkézett vezérlőn kívül más labelable leszármazottat), és a
                böngésző viselkedése ilyenkor nem garantált. A címke szövege
                ettől még kapcsolja a jelölőt (htmlFor), a gombok pedig a
                saját műveletüket végzik.

                Ez az út már egyszer elhasalt: a QA B10/B11 tétele szerint a
                két link natívon nem volt megnyitható (target="_blank" néma
                no-op a WKWebView-ban) — az Apple szemében önálló elutasítási
                ok. ClickUp: 123qmzqyahe */}
            {mode === "register" && (
              <div className="flex items-start gap-2.5">
                <input
                  id="gdpr-accept"
                  type="checkbox"
                  checked={gdprAccepted}
                  onChange={e => setGdprAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                />
                <span className="text-xs text-gray-500 leading-relaxed">
                  <label htmlFor="gdpr-accept" className="cursor-pointer">
                    Elolvastam és elfogadom az{" "}
                  </label>
                  <button
                    type="button"
                    onClick={() => openExternal("/adatvedelmi-iranyelvek.html")}
                    className="text-emerald-700 font-medium underline"
                  >
                    adatvédelmi irányelveket
                  </button>
                  {" "}és a{" "}
                  <button
                    type="button"
                    onClick={() => openExternal("/felhasznalasi-feltetelek.html")}
                    className="text-emerald-700 font-medium underline"
                  >
                    felhasználási feltételeket
                  </button>.
                </span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (mode === "register" && !gdprAccepted)}
              className="w-full bg-emerald-700 text-white py-3 rounded-xl font-semibold text-sm hover:bg-emerald-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              ) : mode === "login" ? (
                <><LogIn size={16} /> Bejelentkezés</>
              ) : (
                <><UserPlus size={16} /> Fiók létrehozása</>
              )}
            </button>
          </form>

          {mode === "register" && (
            <p className="text-center text-xs text-gray-400">
              Regisztrációval elfogadod az{" "}
              <span className="text-emerald-700 font-medium">adatvédelmi szabályzatot</span>.
              <br />Regisztráció után email megerősítés szükséges.
            </p>
          )}
        </div>
      )}

      <p className="text-xs text-gray-300 mt-6">GLP Napló</p>
    </div>
  );
}
